import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom-v5-compat';

import { ConfigStepBody } from './config-step/config-step';
import { InventoryConfigStepFooter } from './config-step/create-config-step';
import { fieldsTranslations, messages } from './messages';
import { actionMap, initialState } from './reducer';
import { ImportType } from '../../../../../../components/csv-import/data/types/import-type';
import { StepType } from '../../../../../../components/csv-import/data/types/step-type';
import { ImportProvider } from '../../../../../../components/csv-import/ui/components/import-context';
import { SingleImportDrawer } from '../../../../../../components/csv-import/ui/components/single-import-drawer/single-import-drawer';
import { CreateMappingStepBody } from '../../../../../../components/csv-import/ui/components/steps/create-mapping-step/create-mapping-step';
import { CreateMappingStepFooter } from '../../../../../../components/csv-import/ui/components/steps/create-mapping-step/create-mapping-step-footer';
import {
  ImportIsProcessingStepBody,
  ImportIsProcessingStepFooter,
} from '../../../../../../components/csv-import/ui/components/steps/import-is-processing-step/import-is-processing-step';
import { getTranslationFn } from '../../../../../../components/csv-import/ui/utils';
import { useEnableInventoryLocationsBarcode } from '../../../../../../feature-flags/hooks/use-enable-inventory-locations-barcode';
import { useEnableLotTracking } from '../../../../../../feature-flags/hooks/use-enable-lot-tracking';
import { CSV_INVENTORY_IMPORT_JOB_COMPLETE } from '../../../../../../services/pusher-service/pusher-contract-messages';
import Global from '../../../../../../store/global-selectors';
import { Workflows } from '../../../../../../tracking/tracking-constants';
import { WorkflowSteps } from '../../../../../../tracking/use-workflow-tracking';

const steps = [
  {
    id: StepType.CREATE_CONFIG,
    title: <FormattedMessage {...messages.createConfigStep} />,
    body: <ConfigStepBody />,
    footer: <InventoryConfigStepFooter />,
  },
  {
    id: StepType.CREATE_MAPPING,
    title: <FormattedMessage {...messages.createMappingStep} />,
    body: <CreateMappingStepBody />,
    footer: <CreateMappingStepFooter />,
  },
  {
    id: StepType.IMPORT_IS_PROCESSING,
    title: <FormattedMessage {...messages.importResult} />,
    body: (
      <ImportIsProcessingStepBody
        successMessage={<FormattedMessage {...messages.importResultSuccess} />}
        title={<FormattedMessage {...messages.importResult} />}
      />
    ),
    footer: <ImportIsProcessingStepFooter />,
  },
];

const LOT_TRACKING_ATTRIBUTES = ['lot', 'expiration_date'];
const LOCATION_BARCODE_ATTRIBUTE = 'barcode';

const translateField = getTranslationFn(fieldsTranslations, ImportType.inventory);

const createCustomImportSettings = (form) => {
  return {
    inventoryWarehouseId: form.inventoryWarehouse,
    stockImportType: form.stockImportType,
  };
};

/**
 * @param {object} props
 * @param {() => void} props.onClose
 * @param {(step: string, metadata?: object) => void} props.trackProcess
 * @returns {React.JSX.Element}
 */
export const InventoryImportDrawer = ({ onClose, trackProcess }) => {
  const lotTrackingEnabled = useEnableLotTracking();
  const locationBarcodeEnabled = useEnableInventoryLocationsBarcode();
  const { inventoryWarehouseId } = useParams();
  const warehouseLocations = useSelector(Global.selectSellerInventoryWarehouses);
  const onCancel = (step) => {
    return () => {
      onClose();
      trackProcess(WorkflowSteps.CANCEL, { step });
    };
  };

  const attributesToHide = useMemo(() => {
    const hiddenAttributes = [];
    if (!lotTrackingEnabled) {
      hiddenAttributes.push(...LOT_TRACKING_ATTRIBUTES);
    }
    if (!locationBarcodeEnabled) {
      hiddenAttributes.push(LOCATION_BARCODE_ATTRIBUTE);
    }
    return hiddenAttributes;
  }, [lotTrackingEnabled, locationBarcodeEnabled]);

  const init = (initState) => {
    if (inventoryWarehouseId) {
      const warehouse = warehouseLocations.find((w) => w.inventoryWarehouseId === inventoryWarehouseId);
      if (warehouse) {
        return {
          ...initState,
          inventoryWarehouse: inventoryWarehouseId,
        };
      }
    }
    return initState;
  };

  return (
    <ImportProvider
      actionMap={actionMap}
      attributesToHide={attributesToHide}
      createCustomImportSettings={createCustomImportSettings}
      importType={ImportType.inventory}
      init={init}
      initialState={initialState}
      onClose={onCancel(StepType.IMPORT_IS_PROCESSING)}
      pusherEvent={CSV_INVENTORY_IMPORT_JOB_COMPLETE}
      trackProcess={trackProcess}
      translateField={translateField}
      workflowType={Workflows.CSV_INVENTORY_IMPORT_FLOW}
    >
      <SingleImportDrawer header={<FormattedMessage {...messages.modalDrawer} />} onCancel={onCancel} steps={steps} />
    </ImportProvider>
  );
};
